import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Search, ShoppingBag, Menu, X, Heart, User } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAdminMode } from '../context/AdminModeContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { getCategories } from '../services/categoryService';
import { getSiteContent } from '../services/siteContentService';
import { getCollections } from '../services/collectionService';

const Header = ({ onOpenRegionSelector }) => {
  const headerRef = useRef(null);
  const [shopDropdownOpen, setShopDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { getCartCount, toggleCart } = useCart();
  const { wishlistCount } = useWishlist();
  const { adminModeEnabled } = useAdminMode();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isSignedInCustomer = !!currentUser && !currentUser.isAnonymous;
  const returnTo = `${location.pathname}${location.search || ''}`;

  const [categories, setCategories] = useState(() => {
    try {
      return getCategories();
    } catch {
      return [];
    }
  });

  const [collections, setCollections] = useState(() => {
    try {
      return getCollections();
    } catch {
      return [];
    }
  });

  const [siteContent, setSiteContent] = useState(() => {
    try {
      return getSiteContent();
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const refresh = () => {
      try {
        setCategories(getCategories());
      } catch {
        setCategories([]);
      }
    };

    refresh();
    window.addEventListener('ditris:categories', refresh);
    return () => window.removeEventListener('ditris:categories', refresh);
  }, []);

  useEffect(() => {
    const refresh = () => {
      try {
        setCollections(getCollections());
      } catch {
        setCollections([]);
      }
    };

    refresh();
    window.addEventListener('ditris:collections', refresh);
    return () => window.removeEventListener('ditris:collections', refresh);
  }, []);

  useEffect(() => {
    const refresh = () => {
      try {
        setSiteContent(getSiteContent());
      } catch {
        setSiteContent(null);
      }
    };

    refresh();
    window.addEventListener('ditris:siteContent', refresh);
    return () => window.removeEventListener('ditris:siteContent', refresh);
  }, []);

  const announcement = siteContent?.announcement;

  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof document === 'undefined') return;

    const setVar = () => {
      const h = el.offsetHeight || 0;
      try {
        document.documentElement.style.setProperty('--header-offset', `${h}px`);
      } catch {
        // ignore
      }
    };

    setVar();

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => setVar());
      ro.observe(el);
    }

    window.addEventListener('resize', setVar);
    return () => {
      try {
        window.removeEventListener('resize', setVar);
      } catch {
        // ignore
      }
      try {
        if (ro) ro.disconnect();
      } catch {
        // ignore
      }
    };
  }, []);

  return (
    <header ref={headerRef} className="sticky top-0 bg-white z-50 border-b border-gray-200">
      {announcement?.enabled && announcement?.text ? (
        <div className="bg-black text-white">
          <div className="px-6 py-2 text-center text-xs tracking-widest font-medium">
            {announcement.link ? (
              <Link to={announcement.link} className="hover:opacity-80 transition-opacity">
                {announcement.text}
              </Link>
            ) : (
              announcement.text
            )}
          </div>
        </div>
      ) : null}
      {/* Main Header */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left Section - Logo & Navigation */}
          <div className="flex items-center gap-8">
            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Logo */}
            <Link to="/home" className="flex items-center">
              <img
                src="/logos/logo.png"
                alt="Ditris"
                className="h-8 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {/* Shop Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setShopDropdownOpen(true)}
                onMouseLeave={() => setShopDropdownOpen(false)}
              >
                <button className="flex items-center gap-1 text-sm font-medium tracking-wide hover:opacity-60 transition-opacity">
                  SHOP
                  <ChevronDown size={16} />
                </button>
                {shopDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 shadow-lg min-w-[200px]">
                    {categories.map(category => (
                      <Link
                        key={category.id}
                        to={`/shop/${category.slug}`}
                        className="block px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
                      >
                        {category.name}
                      </Link>
                    ))}
                    {collections.length > 0 ? (
                      <div className="border-t border-gray-200 py-2">
                        <div className="px-4 py-1 text-[11px] tracking-widest text-gray-500">COLLECTIONS</div>
                        {collections.map((c) => (
                          <Link
                            key={c.slug}
                            to={`/collections/${c.slug}`}
                            className="block px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
                          >
                            {c.name}
                          </Link>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <Link
                to="/about"
                className="text-sm font-medium tracking-wide hover:opacity-60 transition-opacity"
              >
                ABOUT
              </Link>

              <Link
                to="/contact"
                className="text-sm font-medium tracking-wide hover:opacity-60 transition-opacity"
              >
                CONTACT
              </Link>

              <Link
                to="/orders"
                className="text-sm font-medium tracking-wide hover:opacity-60 transition-opacity"
              >
                ORDERS
              </Link>
            </nav>
          </div>

          {/* Right Section - Search, Currency, Bag */}
          <div className="flex items-center gap-6">
            {adminModeEnabled && (
              <div className="hidden lg:flex items-center gap-3">
                <span className="text-[11px] tracking-widest font-medium text-gray-600 border border-gray-200 rounded-full px-3 py-1">
                  ADMIN MODE
                </span>

                <Link
                  to="/admin/products/new"
                  className="text-xs font-medium tracking-wider border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors"
                >
                  ADD PRODUCT
                </Link>
                <Link
                  to="/admin/products"
                  className="text-xs font-medium tracking-wider border border-gray-300 px-4 py-2 hover:bg-gray-50 transition-colors"
                >
                  LISTED PRODUCTS
                </Link>
                <Link
                  to="/admin/categories"
                  className="text-xs font-medium tracking-wider border border-gray-300 px-4 py-2 hover:bg-gray-50 transition-colors"
                >
                  CATEGORIES
                </Link>
                <Link
                  to="/admin/collections"
                  className="text-xs font-medium tracking-wider border border-gray-300 px-4 py-2 hover:bg-gray-50 transition-colors"
                >
                  COLLECTIONS
                </Link>
                <Link
                  to="/admin/orders"
                  className="text-xs font-medium tracking-wider border border-gray-300 px-4 py-2 hover:bg-gray-50 transition-colors"
                >
                  ORDERS
                </Link>
              </div>
            )}

            <button
              type="button"
              onClick={() => navigate('/search')}
              className="md:hidden inline-flex items-center hover:opacity-60 transition-opacity"
              aria-label="Search"
            >
              <Search size={20} />
            </button>

            <button
              type="button"
              onClick={() => navigate('/search')}
              className="hidden md:inline-flex text-sm font-medium tracking-wide hover:opacity-60 transition-opacity"
            >
              SEARCH
            </button>

            <button
              onClick={() => navigate('/wishlist')}
              className="flex items-center gap-2 text-sm font-medium tracking-wide hover:opacity-60 transition-opacity relative"
              aria-label="Wishlist"
            >
              <Heart size={20} />
              {wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </button>

            <button
              onClick={toggleCart}
              className="flex items-center gap-2 text-sm font-medium tracking-wide hover:opacity-60 transition-opacity relative"
              aria-label="Bag"
            >
              <ShoppingBag size={20} />
              <span className="hidden md:inline">BAG</span>
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </button>

            <button
              onClick={() => {
                if (isSignedInCustomer) {
                  navigate('/profile');
                } else {
                  navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
                }
              }}
              className="flex items-center gap-2 text-sm font-medium tracking-wide hover:opacity-60 transition-opacity"
              aria-label={isSignedInCustomer ? 'Profile' : 'Sign in'}
            >
              <User size={20} />
              <span className="hidden md:inline">{isSignedInCustomer ? 'PROFILE' : 'SIGN IN'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Category Navigation - Desktop */}
      <div className="hidden lg:block border-t border-gray-200">
        <div className="px-6 py-3 flex items-center gap-6 overflow-x-auto">
          {categories.map(category => (
            <Link
              key={category.id}
              to={`/shop/${category.slug}`}
              className="text-xs font-medium tracking-wider whitespace-nowrap hover:opacity-60 transition-opacity"
            >
              {category.name}
            </Link>
          ))}
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white">
          <div className="px-6 py-4 space-y-4">
            {adminModeEnabled && (
              <div className="space-y-3 pb-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] tracking-widest font-medium text-gray-600 border border-gray-200 rounded-full px-3 py-1">
                    ADMIN MODE
                  </div>
                </div>
                <Link
                  to="/admin/products/new"
                  className="block text-sm font-medium border border-black px-4 py-3 text-center hover:bg-black hover:text-white transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  ADD PRODUCT
                </Link>
                <Link
                  to="/admin/products"
                  className="block text-sm font-medium border border-gray-300 px-4 py-3 text-center hover:bg-gray-50 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  LISTED PRODUCTS
                </Link>
                <Link
                  to="/admin/categories"
                  className="block text-sm font-medium border border-gray-300 px-4 py-3 text-center hover:bg-gray-50 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  CATEGORIES
                </Link>
                <Link
                  to="/admin/collections"
                  className="block text-sm font-medium border border-gray-300 px-4 py-3 text-center hover:bg-gray-50 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  COLLECTIONS
                </Link>
                <Link
                  to="/admin/orders"
                  className="block text-sm font-medium border border-gray-300 px-4 py-3 text-center hover:bg-gray-50 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  ORDERS
                </Link>
              </div>
            )}
            <div className="space-y-2">
              <div className="font-medium text-sm mb-2">SHOP</div>
              {categories.map(category => (
                <Link
                  key={category.id}
                  to={`/shop/${category.slug}`}
                  className="block text-sm py-1 hover:opacity-60 transition-opacity"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {category.name}
                </Link>
              ))}
            </div>
            {collections.length > 0 ? (
              <div className="space-y-2">
                <div className="font-medium text-sm mb-2">COLLECTIONS</div>
                {collections.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/collections/${c.slug}`}
                    className="block text-sm py-1 hover:opacity-60 transition-opacity"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            ) : null}
            <Link
              to="/wishlist"
              className="block text-sm font-medium hover:opacity-60 transition-opacity"
              onClick={() => setMobileMenuOpen(false)}
            >
              WISHLIST
            </Link>
            <button
              type="button"
              className="block text-left w-full text-sm font-medium hover:opacity-60 transition-opacity"
              onClick={() => {
                setMobileMenuOpen(false);
                toggleCart();
              }}
            >
              BAG
            </button>
            <Link
              to={isSignedInCustomer ? '/profile' : `/login?returnTo=${encodeURIComponent(returnTo)}`}
              className="block text-sm font-medium hover:opacity-60 transition-opacity"
              onClick={() => setMobileMenuOpen(false)}
            >
              {isSignedInCustomer ? 'PROFILE' : 'SIGN IN'}
            </Link>
            <Link
              to="/about"
              className="block text-sm font-medium hover:opacity-60 transition-opacity"
              onClick={() => setMobileMenuOpen(false)}
            >
              ABOUT
            </Link>
            <Link
              to="/contact"
              className="block text-sm font-medium hover:opacity-60 transition-opacity"
              onClick={() => setMobileMenuOpen(false)}
            >
              CONTACT
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;